/**
 * patientCodename — a stable, memorable, pronounceable codename for a patient,
 * derived deterministically from the patient's opaque `token` (== patient id,
 * see api-service-go patients.go `Token: p.ID`).
 *
 * WHY this exists
 * ---------------
 * A patient has NO name (PHI rule — see patientLabel.ts). The previous display
 * label was the `{ward}-{bed}` *placement* string. That has two defects:
 *   1. It is long ("Medizinische Intensivstation-42").
 *   2. It derives the patient's IDENTITY from their MUTABLE placement, so the
 *      moment a patient moves bed or ward the name the nurse memorised changes
 *      under her — a moving target the eye can never anchor on.
 *
 * A codename derived from the IMMUTABLE token is short, pronounceable,
 * memorable, and STABLE for the whole stay, while staying fully anonymous.
 *
 * SCHEME
 * ------
 * An `{adjective} {animal} {code}` callsign — the "human-friendly unique id"
 * pattern proven by Docker / Heroku / gfycat. Chosen over the NATO/ICAO
 * phonetic alphabet (only 26 words, so the memorable component repeats
 * constantly on a ward). Word lists are curated to dignified, neutral,
 * cross-language pronounceable terms — never anything that could read as a
 * judgement about a patient (no body-shape, no comedic or pejorative animals).
 *
 * These lists are fixed REFERENCE DATA for the scheme (like the NATO alphabet
 * is a fixed standard), not an operational tunable — hence bundled here, not in
 * app.yaml.
 *
 * UNIQUENESS is intentionally probabilistic, NOT enforced against the current
 * census. The codename is a stable *display* aid; the system always acts on the
 * token, never on the rendered string. Enforcing render-time uniqueness would
 * require every view to know its full patient set and bump the `{code}` on
 * collision — which would make the SAME patient render a DIFFERENT codename in
 * different views, destroying the stability that is the whole point. Instead the
 * `{code}` tail (2 Crockford chars from the token) makes the full codename's
 * collision rate negligible: the `{adjective} {animal}` pair alone would
 * duplicate ~16% of the time across a full 40-bed ward (small word lists,
 * birthday paradox), but with `{code}` the full-codename collision is <1% at 40
 * patients (0 collisions observed over 4000 × 40-patient trials).
 */

// Dignified, neutral, easy-to-say adjectives: positive temperament + colour +
// nature. No word that could editorialise a patient's body or character.
const ADJECTIVES = [
  'swift', 'calm', 'bright', 'bold', 'brave', 'keen', 'gentle', 'clever',
  'agile', 'noble', 'loyal', 'hardy', 'lively', 'merry', 'steady', 'nimble',
  'humble', 'mellow', 'serene', 'placid', 'spry', 'lucid', 'plucky', 'cheery',
  'wise', 'kind', 'warm', 'glad', 'neat', 'fleet', 'sound', 'eager', 'alert',
  'quiet', 'sunny', 'jolly', 'deft', 'sprightly', 'stalwart', 'gallant',
  'amber', 'azure', 'coral', 'jade', 'ivory', 'olive', 'slate', 'teal',
  'hazel', 'silver', 'golden', 'copper', 'crimson', 'scarlet', 'violet',
  'cyan', 'indigo', 'cobalt', 'russet', 'sienna', 'saffron', 'maroon',
  'bronze', 'pearl', 'ochre', 'ember', 'frost', 'storm', 'river', 'cedar',
  'willow', 'aspen', 'maple', 'laurel', 'dawn', 'dusk', 'misty', 'snowy',
  'breezy', 'autumn', 'summer', 'winter', 'dewy', 'starry', 'sandy', 'mossy',
  'sylvan', 'rowan', 'birch', 'alder',
] as const;

// Dignified, recognisable, graceful animals: noble birds, cats, antelopes,
// sea life. Deliberately excludes anything with a negative idiom or comedic
// connotation (weasel, sloth, walrus, pig, …) — a patient must never be given
// an undignified callsign.
const ANIMALS = [
  'falcon', 'eagle', 'hawk', 'heron', 'egret', 'swan', 'crane', 'raven',
  'robin', 'wren', 'lark', 'finch', 'owl', 'osprey', 'kestrel', 'merlin',
  'harrier', 'condor', 'ibis', 'oriole', 'martin', 'swallow', 'kingfisher',
  'sparrow', 'starling', 'magpie', 'jay', 'dove', 'quail', 'pheasant', 'kite',
  'plover', 'puffin', 'tern', 'gannet',
  'lynx', 'fox', 'hare', 'otter', 'marten', 'ibex', 'chamois', 'gazelle',
  'oryx', 'kudu', 'impala', 'springbok', 'panther', 'jaguar', 'leopard',
  'cheetah', 'cougar', 'puma', 'ocelot', 'serval', 'caracal', 'lion', 'tiger',
  'wolf', 'deer', 'roe', 'elk', 'moose', 'bison', 'badger', 'beaver', 'mink',
  'ermine', 'stoat', 'marmot', 'lemur', 'panda', 'sable', 'ibisbill',
  'dolphin', 'seal', 'orca', 'narwhal', 'marlin', 'manta', 'tarpon', 'mako',
  'albatross', 'petrel', 'curlew', 'avocet', 'redshank', 'sandpiper',
  'goldcrest', 'firecrest', 'nightjar', 'nuthatch', 'chaffinch', 'siskin',
] as const;

// Crockford base32 alphabet (no I/L/O/U) — unambiguous when read aloud or
// transcribed, for the strictly-varying `{code}` component.
const CROCKFORD_BASE32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

// Slot separator between token and salt in the hash input. A plain visible
// char — NOT a control byte: a NUL here makes git treat the whole file as
// binary and hides the module from PR review.
const SLOT_SEP = ':';

/**
 * FNV-1a 32-bit hash. Deterministic, well-distributed, dependency-free — the
 * codename must be identical everywhere the same token is rendered.
 */
function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Pick a list entry from a salted hash of the token (salt decorrelates slots). */
function pick(list: readonly string[], token: string, salt: string): string {
  return list[fnv1a32(`${token}${SLOT_SEP}${salt}`) % list.length];
}

const capitalize = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1);

/** `len` Crockford-base32 chars from a salted token hash. */
function shortCode(token: string, len = 2): string {
  let hash = fnv1a32(`${token}${SLOT_SEP}code`);
  let out = '';
  for (let i = 0; i < len; i++) {
    out += CROCKFORD_BASE32[hash % 32];
    hash = Math.floor(hash / 32);
  }
  return out;
}

/**
 * Default codename shape. A documented correctness-coupling constant: the
 * template's placeholders are bound to this generator's vocabulary
 * (`{adjective}`, `{animal}`, `{code}`), so it is not a free-form tunable.
 *
 * The `{code}` tail is the goal's "at least one component that is unique" (see
 * the collision note in the module header). Drop `{code}` here for the pure
 * two-word look if a deployment prefers it (and accepts the higher ward-wide
 * collision rate).
 */
export const DEFAULT_CODENAME_FORMAT = '{adjective} {animal} {code}';

/**
 * Render a patient's stable codename from their token. Same token → same
 * codename, forever, regardless of ward/bed moves. `format` interpolates
 * `{adjective}`, `{animal}`, `{code}`; unknown patients (no token) get an
 * em-dash so a row is never blank.
 */
export function patientCodename(
  token: string | undefined | null,
  format: string = DEFAULT_CODENAME_FORMAT,
): string {
  if (!token) return '—';
  return format
    .replace(/\{adjective\}/g, capitalize(pick(ADJECTIVES, token, 'adj')))
    .replace(/\{animal\}/g, capitalize(pick(ANIMALS, token, 'animal')))
    .replace(/\{code\}/g, shortCode(token))
    .trim();
}
